import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { CloudRain, Calendar, TrendingUp, X, Droplets } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line,
  Area
} from "recharts";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format volume to readable string
const formatVolume = (volume_m3) => {
  if (volume_m3 >= 1000000) {
    return `${(volume_m3 / 1000000).toFixed(2)} hm³`;
  }
  if (volume_m3 >= 1000) {
    return `${(volume_m3 / 1000).toFixed(0)}k m³`;
  }
  return `${volume_m3.toLocaleString('es-ES', { maximumFractionDigits: 0 })} m³`;
};

// Spanish month names
const MONTH_NAMES_ES = {
  'Jan': 'Ene', 'Feb': 'Feb', 'Mar': 'Mar', 'Apr': 'Abr',
  'May': 'May', 'Jun': 'Jun', 'Jul': 'Jul', 'Aug': 'Ago',
  'Sep': 'Sep', 'Oct': 'Oct', 'Nov': 'Nov', 'Dec': 'Dic'
};

const PrecipitationChart = ({ isOpen, onClose }) => {
  const [monthlyData, setMonthlyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('precipitation'); // 'precipitation' | 'water' | 'byDam'

  useEffect(() => {
    if (isOpen) {
      fetchMonthlyData();
    }
  }, [isOpen]);

  const fetchMonthlyData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/weather/precipitation-monthly?months=12`);
      setMonthlyData(response.data);
    } catch (err) {
      console.error("Error fetching monthly precipitation:", err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Prepare chart data
  const chartData = monthlyData?.monthly_data?.map(m => ({
    month: MONTH_NAMES_ES[m.month_short] || m.month_short,
    year: m.year,
    label: `${MONTH_NAMES_ES[m.month_short] || m.month_short} ${m.year.toString().slice(-2)}`,
    precipitacion: m.precipitation_mm,
    diasLluvia: m.rainy_days,
    aguaTotal: m.total_water_m3 / 1000, // Convert to thousands m³
    parralillo: m.water_by_dam.PARRALILLO / 1000,
    siberio: m.water_by_dam.SIBERIO / 1000,
    caidero: m.water_by_dam.CAIDERO / 1000,
  })) || [];

  // Calculate totals
  const totalPrecip = chartData.reduce((sum, d) => sum + d.precipitacion, 0);
  const totalWater = chartData.reduce((sum, d) => sum + d.aguaTotal * 1000, 0);
  const totalRainyDays = chartData.reduce((sum, d) => sum + d.diasLluvia, 0);

  // Use portal to render at document.body level
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm" data-testid="precipitation-chart-modal">
      <div className="bg-[#0a0f18] border border-blue-900/50 rounded-xl w-[95vw] max-w-5xl max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-blue-900/30 bg-gradient-to-r from-blue-900/30 to-teal-900/20">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-600/30 rounded-lg">
              <CloudRain className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white uppercase tracking-wide">Histórico de Precipitaciones</h2>
              <p className="text-xs text-zinc-400">Últimos 12 meses - Año hidrológico {monthlyData?.current_hydrological_year}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-zinc-400 hover:text-white hover:bg-zinc-800"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-pulse text-zinc-500">Cargando datos históricos...</div>
            </div>
          ) : (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                <Card className="bg-blue-900/20 border-blue-800/30 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <CloudRain className="w-4 h-4 text-blue-400" />
                    <span className="text-xs text-zinc-400 uppercase">Precipitación Total</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-300 font-mono">{totalPrecip.toFixed(1)} mm</p>
                </Card>
                <Card className="bg-teal-900/20 border-teal-800/30 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Droplets className="w-4 h-4 text-teal-400" />
                    <span className="text-xs text-zinc-400 uppercase">Agua Aportada</span>
                  </div>
                  <p className="text-2xl font-bold text-teal-300 font-mono">{formatVolume(totalWater)}</p>
                </Card>
                <Card className="bg-amber-900/20 border-amber-800/30 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="w-4 h-4 text-amber-400" />
                    <span className="text-xs text-zinc-400 uppercase">Días con Lluvia</span>
                  </div>
                  <p className="text-2xl font-bold text-amber-300 font-mono">{totalRainyDays}</p>
                </Card>
                <Card className="bg-zinc-800/50 border-zinc-700/30 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="w-4 h-4 text-zinc-400" />
                    <span className="text-xs text-zinc-400 uppercase">Media Mensual</span>
                  </div>
                  <p className="text-2xl font-bold text-zinc-300 font-mono">{(totalPrecip / 12).toFixed(1)} mm</p>
                </Card>
              </div>

              {/* View Mode Tabs */}
              <div className="flex gap-2 mb-4">
                <Button
                  variant={viewMode === 'precipitation' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('precipitation')}
                  className={viewMode === 'precipitation' ? 'bg-blue-600 text-white' : 'border-zinc-700 text-zinc-400'}
                >
                  <CloudRain className="w-4 h-4 mr-1" />
                  Precipitación
                </Button>
                <Button
                  variant={viewMode === 'water' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('water')}
                  className={viewMode === 'water' ? 'bg-teal-600 text-white' : 'border-zinc-700 text-zinc-400'}
                >
                  <Droplets className="w-4 h-4 mr-1" />
                  Agua Total
                </Button>
                <Button
                  variant={viewMode === 'byDam' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('byDam')}
                  className={viewMode === 'byDam' ? 'bg-emerald-600 text-white' : 'border-zinc-700 text-zinc-400'}
                >
                  <TrendingUp className="w-4 h-4 mr-1" />
                  Por Embalse
                </Button>
              </div>

              {/* Chart */}
              <Card className="bg-zinc-900/50 border-zinc-800/50 p-4">
                <ResponsiveContainer width="100%" height={350}>
                  {viewMode === 'precipitation' ? (
                    <ComposedChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="label" tick={{ fill: '#888', fontSize: 11 }} />
                      <YAxis 
                        yAxisId="left"
                        tick={{ fill: '#888', fontSize: 11 }} 
                        label={{ value: 'mm', angle: -90, position: 'insideLeft', fill: '#888' }}
                      />
                      <YAxis 
                        yAxisId="right"
                        orientation="right"
                        tick={{ fill: '#888', fontSize: 11 }} 
                        label={{ value: 'días', angle: 90, position: 'insideRight', fill: '#888' }}
                      />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: '8px' }}
                        labelStyle={{ color: '#fff' }}
                        formatter={(value, name) => {
                          if (name === 'precipitacion') return [`${value.toFixed(1)} mm`, 'Precipitación'];
                          if (name === 'diasLluvia') return [`${value} días`, 'Días lluvia'];
                          return [value, name];
                        }}
                      />
                      <Legend 
                        formatter={(value) => {
                          if (value === 'precipitacion') return 'Precipitación (mm)';
                          if (value === 'diasLluvia') return 'Días con lluvia';
                          return value;
                        }}
                      />
                      <Bar yAxisId="left" dataKey="precipitacion" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      <Line yAxisId="right" type="monotone" dataKey="diasLluvia" stroke="#f59e0b" strokeWidth={2} dot={{ fill: '#f59e0b' }} />
                    </ComposedChart>
                  ) : viewMode === 'water' ? (
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="label" tick={{ fill: '#888', fontSize: 11 }} />
                      <YAxis 
                        tick={{ fill: '#888', fontSize: 11 }} 
                        label={{ value: 'miles m³', angle: -90, position: 'insideLeft', fill: '#888' }}
                      />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: '8px' }}
                        labelStyle={{ color: '#fff' }}
                        formatter={(value) => [`${(value * 1000).toLocaleString('es-ES')} m³`, 'Agua aportada']}
                      />
                      <Bar dataKey="aguaTotal" fill="#14b8a6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  ) : (
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="label" tick={{ fill: '#888', fontSize: 11 }} />
                      <YAxis 
                        tick={{ fill: '#888', fontSize: 11 }} 
                        label={{ value: 'miles m³', angle: -90, position: 'insideLeft', fill: '#888' }}
                      />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: '8px' }}
                        labelStyle={{ color: '#fff' }}
                        formatter={(value, name) => {
                          const names = { parralillo: 'Parralillo', siberio: 'Siberio', caidero: 'Caidero' };
                          return [`${(value * 1000).toLocaleString('es-ES')} m³`, names[name] || name];
                        }}
                      />
                      <Legend 
                        formatter={(value) => {
                          const names = { parralillo: 'Parralillo', siberio: 'Siberio', caidero: 'Caidero' };
                          return names[value] || value;
                        }}
                      />
                      <Bar dataKey="parralillo" stackId="a" fill="#10b981" />
                      <Bar dataKey="siberio" stackId="a" fill="#f59e0b" />
                      <Bar dataKey="caidero" stackId="a" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </Card>

              {/* Monthly Table */}
              <Card className="bg-zinc-900/50 border-zinc-800/50 p-4 mt-4">
                <h3 className="text-sm font-bold text-zinc-300 uppercase mb-3">Detalle Mensual</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-zinc-800">
                        <th className="text-left text-zinc-500 py-2 px-2">Mes</th>
                        <th className="text-right text-zinc-500 py-2 px-2">Lluvia</th>
                        <th className="text-right text-zinc-500 py-2 px-2">Días</th>
                        <th className="text-right text-emerald-500 py-2 px-2">Parralillo</th>
                        <th className="text-right text-amber-500 py-2 px-2">Siberio</th>
                        <th className="text-right text-cyan-500 py-2 px-2">Caidero</th>
                        <th className="text-right text-blue-400 py-2 px-2">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {chartData.map((row, i) => (
                        <tr key={i} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                          <td className="text-zinc-300 py-2 px-2 font-medium">{row.label}</td>
                          <td className="text-right text-blue-300 py-2 px-2 font-mono">{row.precipitacion.toFixed(1)} mm</td>
                          <td className="text-right text-amber-300 py-2 px-2 font-mono">{row.diasLluvia}</td>
                          <td className="text-right text-emerald-300 py-2 px-2 font-mono">{formatVolume(row.parralillo * 1000)}</td>
                          <td className="text-right text-amber-300 py-2 px-2 font-mono">{formatVolume(row.siberio * 1000)}</td>
                          <td className="text-right text-cyan-300 py-2 px-2 font-mono">{formatVolume(row.caidero * 1000)}</td>
                          <td className="text-right text-blue-300 py-2 px-2 font-mono font-bold">{formatVolume(row.aguaTotal * 1000)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-zinc-800/50 font-bold">
                        <td className="text-zinc-100 py-2 px-2">TOTAL</td>
                        <td className="text-right text-blue-200 py-2 px-2 font-mono">{totalPrecip.toFixed(1)} mm</td>
                        <td className="text-right text-amber-200 py-2 px-2 font-mono">{totalRainyDays}</td>
                        <td className="text-right text-emerald-200 py-2 px-2 font-mono">{formatVolume(chartData.reduce((s, d) => s + d.parralillo * 1000, 0))}</td>
                        <td className="text-right text-amber-200 py-2 px-2 font-mono">{formatVolume(chartData.reduce((s, d) => s + d.siberio * 1000, 0))}</td>
                        <td className="text-right text-cyan-200 py-2 px-2 font-mono">{formatVolume(chartData.reduce((s, d) => s + d.caidero * 1000, 0))}</td>
                        <td className="text-right text-blue-200 py-2 px-2 font-mono">{formatVolume(totalWater)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </Card>

              {/* Note */}
              <p className="text-[10px] text-zinc-600 mt-3 text-center">
                Datos: AEMET (Estación C649I - La Aldea de San Nicolás) | Cálculo hidrológico: Precipitación × Cuenca × Coef. Escorrentía
              </p>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PrecipitationChart;
